<template>
    <section class="form-builder">
        <div class="form-builder__body inner">
            <h2 class="form-title h2">{{ title }}</h2>
            <DSCFText v-if="body" :content="body" class="body" />
        </div>
        <div class="form-builder__form-control inner">
            <div
                v-if="formSubmitSuccess"
                class="form-builder-success"
            >
                <DSNotification
                    type="success"
                    :dismissable="false"
                >
                    {{ $t('form-builder.submit-success') }}
                </DSNotification>
            </div>
            <DSForm
                :id="`form-${id}`"
                :ref="`formBuilder${id}`"
                :name="`form-${id}`"
                class="form-builder__form"
                :class="{'submitted': formSubmitSuccess}"
                :model-value="formFields"
                :rules="formRules"
                @submit="onFormSubmit"
                @update.once="onFormBegin"
                @error="onFormError"
                @is-valid="validity => validForSubmit = validity"
            >
                <fieldset v-if="multipleTopics" class="form-topics">
                    <DSField
                        id="topic"
                        name="topic"
                        :type="formOptions.topicsDisplay"
                        :label="$t('form-builder.topic')"
                        :options="topicOptions"
                    />
                </fieldset>
                <div
                    v-for="field in formBuilderConfiguration.fields"
                    :key="`${id}-${field.name}`"
                    class="field-wrapper"
                >
                    <fieldset v-if="optionInputTypes.includes(field.type)" class="options-input">
                        <legend>{{ field.label }}</legend>
                        <DSField
                            :id="`${id}-${field.name}`"
                            :name="field.name"
                            :type="field.type"
                            :label="field.label"
                            :options="field.options.map(optionLabelToValue) ?? []"
                            :show-validation-error="true"
                        />
                    </fieldset>
                    <fieldset v-else-if="field.type === 'calendar'" class="options-input">
                        <legend>{{ field.label }}</legend>
                        <DSField
                            :id="`${id}-${field.name}`"
                            :name="field.name"
                            :type="field.type"
                            :label="field.label"
                        />
                    </fieldset>
                    <DSField
                        v-else
                        :id="`${id}-${field.name}`"
                        :name="field.name"
                        :type="field.type"
                        :label="field.label"
                        :options="field.options ?? []"
                    />
                </div>
                <DSButton
                    :busy="formSubmitInProgress"
                    type="submit"
                    class="ds-btn ds-btn--primary ds-btn--large"
                >
                    {{ $t('form-builder.submit') }}
                </DSButton>
            </DSForm>
        </div>
    </section>
</template>
<script setup>
import { computed, ref } from 'vue';
import { useI18n, useAnalytics } from '#imports';

const { gtm } = useAnalytics();
const formSubmitHookName = 'dsc:form-builder-submit';
const optionInputTypes = [ 'radio', 'checkbox', 'select' ];

const props = defineProps({
    id: {
        type: String,
        required: false,
        default: '',
    },
    contentType: {
        type: String,
        required: false,
        default: '',
    },
    createdAt: {
        type: String,
        required: false,
        default: '',
    },
    updatedAt: {
        type: String,
        required: false,
        default: '',
    },
    title: {
        type: String,
        required: false,
        default: '',
    },
    body: {
        type: Object,
        required: false,
        default: () => ({}),
    },
    formBuilderConfiguration: {
        type: Object,
        required: false,
        default: () => ({}),
    },
});

const validForSubmit = ref(false);
const formSubmitInProgress = ref(false);
const formSubmitSuccess = ref(false);

const formTopics = computed(() => props.formBuilderConfiguration.topics);
const formOptions = computed(() => props.formBuilderConfiguration.options);
const multipleTopics = computed(() => formTopics.value.length > 1);
const topicOptions = computed(() => formTopics.value.map(({ code: value, label }) => ({ value, label })));

const formFields = ref(props.formBuilderConfiguration.fields.reduce((formFields, field) => {
    if (field.type === 'calendar') {
        formFields[field.name] = null;
    } else if (field.type === 'checkbox' && field.options.length > 1) {
        formFields[field.name] = [];
    } else {
        formFields[field.name] = '';
    }

    return formFields;
}, { topic: topicOptions.value[0].value }));

const formRules = ref(props.formBuilderConfiguration.fields.reduce((formRules, field) => {
    formRules[field.name] = field.validations.map(({ rule: type, value }) => ({ type, value }));

    return formRules;
}, { topic: [ 'notEmpty' ] }));

const optionLabelToValue = option => ({ ...option, value: `${option.label} (${option.value})` });

const parseFieldValue = (field, value) => {
    if (Array.isArray(value)) {
        return value.reduce((result, value) => result.concat(', ', value), '');
    }
    if ([true, false].some(boolean => value === boolean)) {
        const [soleOption] = field.options;

        return `${soleOption.label} (${soleOption.value})`;
    }

    return value;
};

const onFormBegin = () =>{
    gtm().trackEvent({
        event: 'form_begin',
        form_id: `form-${props.id}`,
        form_name: props.title,
        form_description: formFields.value.topic,
    });
};

const onFormError = errors => {
    for (const [, value] of Object.entries(errors)) {
        if (value) {
            gtm().trackEvent({
                event: 'struggle',
                gtm: {
                    error_status: 200,
                    error_code: 'missing required fields',
                    error_text: value,
                    error_type: 'struggle',
                    fatal: 'false',
                },
            });
        }
    }
};

const onFormSubmit = async () => {
    formSubmitInProgress.value = true;
    try {
        const hookBody = {
            entryId: props.id,
            topic: formFields.value.topic,
            fields: props.formBuilderConfiguration.fields.map(field => ({
                name: field.name,
                label: field.label,
                value: parseFieldValue(field, formFields.value[field.name]) ?? '',
            })),
        };
        await useNuxtApp()
            .callHook(formSubmitHookName, hookBody);
        formSubmitSuccess.value = true;
        const event = {
            event: 'form_send',
            form_id: `form-${props.id}`,
            form_name: props.title,
            form_description: formFields.value.topic,
        };

        const expectedEmailFields = ['email', 'sähköpostiosoite'];
        const emailField = Object.keys(formFields.value).find(key => expectedEmailFields.includes(key.toLowerCase()));
        if (emailField) {
            event.email = formFields.value[emailField];
        }

        gtm().trackEvent(event);
    } catch (e) {
        console.error(e);
        onFormError({
            'form-submit': `Form submission failed, ${e.message}`,
        });
    } finally {
        formSubmitInProgress.value = false;
    }
};


useI18n({
    useScope: 'global',
    messages: {
        'fi-FI': {
            'form-builder': {
                'topic': 'Asia',
                'submit': 'Lähetä',
                'submit-success': 'Lomake lähetettiin onnistuneesti',
            },
        },
        'en-EU': {
            'form-builder': {
                'topic': 'Topic',
                'submit': 'Submit',
                'submit-success': 'Form submitted successfully',
            },
        },
        'sv-SE': {
            'form-builder': {
                'topic': 'Ämne',
                'submit': 'Skicka',
                'submit-success': 'Formuläret har skickats',
            },
        },
        'no-NO': {
            'form-builder': {
                'topic': 'Emme',
                'submit': 'Send inn',
                'submit-success': 'Skjemaet ble sendt inn',
            },
        },
    },
});
</script>
<style scoped>
.form-builder {
  display: grid;
  max-width: var(--desktop-max-width);
  width: 100%;
  margin: 0 auto;
}
.form-builder .inner {
  max-width: var(--page-max-width);
  margin: 0 auto;
  padding-left: 1.5rem;
  width: 100%;
  padding-right: 1.5rem;
}
.form-builder {
  margin: 1rem auto 2rem;
}

.form-title {
  margin-bottom: 2rem;
}

.body {
  margin-bottom: 2rem;
}

.form-builder__form-control {
  position: relative;
}

.form-builder__form {
  display: grid;
  gap: 1.5rem;
  transition: filter 0.275ms;
}

.form-builder__form.submitted {
  animation: blur-anim 1s linear forwards;
}

.form-builder-success {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
}

.options-input {
  margin-bottom: 0;
}

@media (min-width: 992px) {
  .form-builder {
    grid-template-columns: repeat(2, 1fr);
  }
}
@keyframes blur-anim {
  100% {
    filter: blur(3px);
  }
}
</style>
